import {
  SELECTED_CURATORS_KEY,
  SELECTED_NETWORKS_KEY,
  SELECTED_PROTOCOLS_KEY,
  SELECTED_TAGS_KEY,
  SELECTED_TOKENS_KEY,
} from '@/constants/state'
import { usePresets } from '@/hooks/usePresets'
import { useVaultsSearchState } from '@/hooks/useVaultsSearchState'
import { cn } from '@/utils/cn'
import { pluralize } from '@/utils/pluralize'
import { useCallback, useMemo, useState } from 'react'
import { Button } from '../common/button'
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogContentWrapper,
  DialogHeader,
  DialogScrollArea,
  DialogTitle,
  DialogTrigger,
} from '../common/dialog'
import { Separator } from '../common/separator'
import { CloseIcon } from '../icons/close'
import { SaveAsIcon } from '../icons/saveAs'
import { PresetNameForm } from './presetNameForm'

const FORM_ID = 'save-preset-form'
const MAX_VISIBLE = 2

function buildChipLabel(word: string, items: string[], plural?: string): string | null {
  if (items.length === 0) return null
  const visible = items.slice(0, MAX_VISIBLE).join(', ')
  const overflow = items.length - MAX_VISIBLE
  const label = pluralize(items.length, word, plural)
  return overflow > 0 ? `${label}: ${visible} +${overflow}` : `${label}: ${visible}`
}

interface Props {
  className?: string
}

export const SavePresetDialog = ({ className }: Props) => {
  const [open, setOpen] = useState(false)
  const [excludedKeys, setExcludedKeys] = useState<Set<string>>(new Set())
  const { searchState, selectedTokens, selectedNetworks, selectedProtocols, selectedCurators, selectedTags } =
    useVaultsSearchState()
  const { savePreset } = usePresets()

  const filterGroups = useMemo(
    () =>
      [
        { key: SELECTED_TOKENS_KEY, label: buildChipLabel('Token', selectedTokens) },
        { key: SELECTED_NETWORKS_KEY, label: buildChipLabel('Network', selectedNetworks) },
        { key: SELECTED_PROTOCOLS_KEY, label: buildChipLabel('Protocol', selectedProtocols) },
        { key: SELECTED_CURATORS_KEY, label: buildChipLabel('Curator', selectedCurators) },
        { key: SELECTED_TAGS_KEY, label: buildChipLabel('Category', selectedTags, 'Categories') },
      ].filter((g) => g.label !== null) as { key: string; label: string }[],
    [selectedTokens, selectedNetworks, selectedProtocols, selectedCurators, selectedTags]
  )

  const visibleChips = useMemo(() => filterGroups.filter((g) => !excludedKeys.has(g.key)), [filterGroups, excludedKeys])

  const handleRemoveChip = useCallback((key: string) => {
    setExcludedKeys((prev) => new Set([...prev, key]))
  }, [])

  const handleOpenChange = useCallback((next: boolean) => {
    setOpen(next)
    if (!next) setExcludedKeys(new Set())
  }, [])

  const onSubmit = useCallback(
    (name: string) => {
      try {
        const params = new URLSearchParams(searchState)
        excludedKeys.forEach((key) => params.delete(key))
        savePreset(name, params)
        setOpen(false)
      } catch (error) {
        console.error('Failed to save preset:', error)
      }
    },
    [searchState, excludedKeys, savePreset]
  )

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <Button variant="input" size="md" className={cn('gap-1 pr-3 pl-2', className)}>
          <SaveAsIcon size={20} /> Save preset
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogBody>
          <DialogScrollArea>
            <DialogContentWrapper className="!pb-3 pt-10 md:pt-16">
              <DialogHeader>
                <DialogTitle>Save filters preset</DialogTitle>
              </DialogHeader>
              <div className="flex flex-col gap-4 rounded-2xl bg-page p-4">
                {visibleChips.length > 0 && (
                  <>
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-medium text-heading-foreground text-sm">Applied filters:</span>
                      {visibleChips.map(({ key, label }) => (
                        <Button
                          key={key}
                          variant="input"
                          className="h-10 gap-2 rounded-full px-3 text-sm"
                          onClick={() => handleRemoveChip(key)}
                        >
                          <span className="text-accent">{label}</span>

                          <span className="text-heading-foreground">
                            <CloseIcon size={16} />
                          </span>
                        </Button>
                      ))}
                    </div>
                    <Separator />
                  </>
                )}
                <PresetNameForm id={FORM_ID} onSubmit={onSubmit} />
              </div>
            </DialogContentWrapper>
          </DialogScrollArea>
          <DialogContentWrapper className="!pt-0">
            <Button form={FORM_ID} type="submit" variant="primary" size="lg" className="w-full">
              Save
            </Button>
          </DialogContentWrapper>
        </DialogBody>
      </DialogContent>
    </Dialog>
  )
}
